import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend
} from "recharts";

const TOOLTIP_STYLE = { background: "#0B172A", border: "1px solid rgba(255,255,255,0.2)", color: "#F4F1EA" };
const TOOLTIP_LABEL_STYLE = { color: "#E8B298" };
const LEGEND_STYLE = { fontSize: 11, color: "#cbd5e1" };
const LINE_DOT = { r: 3 };

const KPI_CARDS = [
  { k: "institutes_onboarded",  t: "Institutes onboarded",   fmt: v => v,  color: "#E8B298" },
  { k: "officials_trained",     t: "Officials trained",       fmt: v => v.toLocaleString("en-IN"), color: "#E8B298" },
  { k: "faculty_discoverable",  t: "Faculty discoverable",    fmt: v => v,  color: "#4C956C" },
  { k: "knowledge_assets",      t: "Knowledge assets",        fmt: v => v,  color: "#4C956C" },
  { k: "programmes_live",       t: "Programmes live",         fmt: v => v,  color: "#0077B6" },
  { k: "avg_competency_gain_pct", t: "Avg. competency gain",  fmt: v => v + "%", color: "#D95D39" },
];

export default function EngineDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/dashboard/stats").then(r => setData(r.data)); }, []);

  if (!data) return <div className="text-cream/60 font-mono-data text-sm">Loading engine…</div>;

  return (
    <div data-testid="engine-dashboard-module" className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {KPI_CARDS.map(c => (
          <div key={c.k} data-testid={`kpi-${c.k}`}
               className="border border-white/10 bg-[#13233F] p-4">
            <div className="font-mono-data text-[10px] uppercase tracking-[0.22em] text-cream/50">{c.t}</div>
            <div className="font-mono-data text-3xl mt-2" style={{color: c.color}}>{c.fmt(data.kpis[c.k])}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div data-testid="chart-participation" className="lg:col-span-2 border border-white/10 bg-[#13233F] p-6 min-h-[380px]">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="font-mono-data text-[10px] uppercase tracking-[0.22em] text-[#E8B298]">Participation flow</div>
              <div className="font-serif-display text-xl mt-1">Designed → Delivered → Certified</div>
            </div>
            <div className="font-mono-data text-[10px] text-cream/50">last 7 months</div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data.participation_trend}>
              <CartesianGrid stroke="rgba(255,255,255,0.08)" strokeDasharray="3 3" />
              <XAxis dataKey="month" stroke="#9aa4b8" fontSize={11} />
              <YAxis yAxisId="left" stroke="#9aa4b8" fontSize={11} />
              <YAxis yAxisId="right" orientation="right" stroke="#9aa4b8" fontSize={11} />
              <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} />
              <Legend wrapperStyle={LEGEND_STYLE} />
              <Line yAxisId="left"  type="monotone" dataKey="designed"  stroke="#D95D39" strokeWidth={2} dot={LINE_DOT} />
              <Line yAxisId="left"  type="monotone" dataKey="delivered" stroke="#0077B6" strokeWidth={2} dot={LINE_DOT} />
              <Line yAxisId="right" type="monotone" dataKey="certified" stroke="#4C956C" strokeWidth={2} dot={LINE_DOT} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div data-testid="chart-delivery" className="border border-white/10 bg-[#13233F] p-6 min-h-[380px]">
          <div className="font-mono-data text-[10px] uppercase tracking-[0.22em] text-[#E8B298]">Delivery mode</div>
          <div className="font-serif-display text-xl mt-1">How programmes reach officials</div>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={data.delivery_breakdown} dataKey="count" nameKey="mode" innerRadius={55} outerRadius={95} paddingAngle={2}>
                {data.delivery_breakdown.map((d,i) => <Cell key={i} fill={d.color} />)}
              </Pie>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={LEGEND_STYLE} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div data-testid="chart-gaps" className="border border-white/10 bg-[#13233F] p-6">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="font-mono-data text-[10px] uppercase tracking-[0.22em] text-[#E8B298]">Competency gaps</div>
            <div className="font-serif-display text-xl mt-1">Demand gap vs. current fill</div>
          </div>
          <div className="font-mono-data text-[10px] text-cream/50">normalized index · 0-100</div>
        </div>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={data.competency_gaps} layout="vertical" barCategoryGap={14}>
            <CartesianGrid stroke="rgba(255,255,255,0.08)" strokeDasharray="3 3" />
            <XAxis type="number" stroke="#9aa4b8" fontSize={11} />
            <YAxis type="category" dataKey="competency" stroke="#9aa4b8" fontSize={11} width={140}/>
            <Tooltip contentStyle={TOOLTIP_STYLE} />
            <Legend wrapperStyle={LEGEND_STYLE} />
            <Bar dataKey="gap"  fill="#D95D39" name="Demand gap" />
            <Bar dataKey="fill" fill="#4C956C" name="Current fill" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
